import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Volume2, Sparkles, Check, Loader2, PlayCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * ConceptTeachCard
 * ----------------
 * A 30-60s "Teach" step shown once per concept before practice starts.
 * - Zippie face + big illustration
 * - Auto-read narration with karaoke word highlight (uses browser SpeechSynthesis if
 *   an ElevenLabs hook is not passed in — this component is intentionally
 *   framework-light so it can drop into any module.)
 * - "I got it!" button unlocks practice.
 *
 * Props:
 *   module       — 'alphabets' | 'reading' | 'math' | 'writing' | 'science'
 *   conceptKey   — snake-case concept id, e.g. 'vowels', 'place_value'
 *   ageGroup     — optional age hint forwarded to the backend
 *   onDone       — called when the child taps "I got it!"
 *   apiBaseUrl   — optional override (defaults to REACT_APP_BACKEND_URL)
 *   storageKey   — optional localStorage flag key; if set and value is present,
 *                  the card auto-dismisses (concept already taught).
 */
const API_BASE = process.env.REACT_APP_BACKEND_URL;

const speakBrowser = (text, onWord) => {
  try {
    if (!('speechSynthesis' in window)) return null;
    window.speechSynthesis.cancel();
    const utter = new SpeechSynthesisUtterance(text);
    utter.rate = 0.92;
    utter.pitch = 1.05;
    if (onWord) {
      utter.onboundary = (ev) => {
        if (ev.name === 'word' && typeof ev.charIndex === 'number') {
          onWord(ev.charIndex);
        }
      };
    }
    window.speechSynthesis.speak(utter);
    return utter;
  } catch { return null; }
};

const ConceptTeachCard = ({
  module,
  conceptKey,
  ageGroup,
  onDone,
  apiBaseUrl,
  storageKey,
  lessonData,
}) => {
  const [loading, setLoading] = useState(true);
  const [lesson, setLesson] = useState(null);
  const [wordIdx, setWordIdx] = useState(-1);
  const [error, setError] = useState(null);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const utterRef = useRef(null);

  // Skip entirely if already taught
  useEffect(() => {
    if (storageKey && typeof window !== 'undefined' && window.localStorage.getItem(storageKey)) {
      onDone && onDone({ skipped: true });
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    let cancelled = false;
    if (lessonData) { setLesson(lessonData); setLoading(false); return undefined; }
    const base = apiBaseUrl || API_BASE;
    const params = ageGroup ? `?age_group=${encodeURIComponent(ageGroup)}` : '';
    setLoading(true);
    axios
      .get(`${base}/api/learn/concept/${module}/${conceptKey}${params}`, { withCredentials: true })
      .then((r) => { if (!cancelled) { setLesson(r.data); setLoading(false); } })
      .catch(() => { if (!cancelled) { setError('Could not load the lesson — you can still practice.'); setLoading(false); } });
    return () => { cancelled = true; if (utterRef.current) window.speechSynthesis?.cancel(); };
  }, [module, conceptKey, ageGroup, apiBaseUrl, lessonData]);

  const words = useMemo(() => (lesson?.narration ? lesson.narration.split(/(\s+)/) : []), [lesson]);

  // Start reading as soon as narration loads
  useEffect(() => {
    if (!lesson?.narration) return;
    // Word-boundary index → word-array index lookup
    let acc = 0;
    const wordStarts = words.map((w) => {
      const start = acc;
      acc += w.length;
      return start;
    });
    setIsSpeaking(true);
    utterRef.current = speakBrowser(lesson.narration, (charIndex) => {
      let hit = 0;
      for (let i = 0; i < wordStarts.length; i++) {
        if (wordStarts[i] <= charIndex) hit = i;
      }
      setWordIdx(hit);
    });
    if (utterRef.current) {
      utterRef.current.onend = () => setIsSpeaking(false);
    } else {
      setIsSpeaking(false);
    }
    // eslint-disable-next-line
  }, [lesson?.narration]);

  const replay = () => {
    if (!lesson?.narration) return;
    setWordIdx(-1);
    setIsSpeaking(true);
    utterRef.current = speakBrowser(lesson.narration, (charIndex) => {
      let acc = 0, hit = 0;
      for (let i = 0; i < words.length; i++) {
        if (acc <= charIndex) hit = i;
        acc += words[i].length;
      }
      setWordIdx(hit);
    });
    if (utterRef.current) utterRef.current.onend = () => setIsSpeaking(false);
  };

  const handleGotIt = () => {
    try { window.speechSynthesis?.cancel(); } catch {}
    if (storageKey && typeof window !== 'undefined') {
      try { window.localStorage.setItem(storageKey, '1'); } catch {}
    }
    onDone && onDone({ skipped: false });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="concept-teach-loading">
        <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
      </div>
    );
  }

  if (error || !lesson) {
    return (
      <div className="text-center py-8" data-testid="concept-teach-error">
        <p className="text-sm text-slate-500 mb-4">{error || 'No lesson available.'}</p>
        <Button onClick={handleGotIt} data-testid="concept-teach-skip-btn">Continue to practice</Button>
      </div>
    );
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.35, ease: [0.2, 0.7, 0.2, 1] }}
        className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-teal-50 via-white to-amber-50 border-4 border-teal-200 shadow-2xl p-6 sm:p-8"
        data-testid="concept-teach-card"
      >
        {/* Zippie / mascot corner (gentle pulse) */}
        <motion.div
          className="absolute -top-2 -right-2 w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-teal-400 to-cyan-500 flex items-center justify-center shadow-lg ring-4 ring-white"
          animate={{ scale: [1, 1.06, 1], rotate: [0, 4, 0, -4, 0] }}
          transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
        >
          <Sparkles className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
        </motion.div>

        {/* Big illustration (floats up-down) */}
        <motion.div
          className="mx-auto mb-4 flex items-center justify-center rounded-2xl bg-white shadow-inner w-full h-40 sm:h-48 text-5xl sm:text-6xl font-fredoka font-bold text-teal-700 select-none whitespace-pre-line text-center leading-tight"
          data-testid="concept-teach-illustration"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1, y: [0, -6, 0] }}
          transition={{
            opacity: { duration: 0.4, delay: 0.1 },
            scale: { duration: 0.4, delay: 0.1 },
            y: { duration: 3.5, repeat: Infinity, ease: 'easeInOut', delay: 0.5 },
          }}
        >
          {lesson.illustration || '✨'}
        </motion.div>

        {/* Title */}
        <motion.h2
          className="font-fredoka text-3xl sm:text-4xl font-bold text-slate-900 text-center mb-4"
          data-testid="concept-teach-title"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
        >
          {lesson.title}
        </motion.h2>

        {/* Karaoke narration */}
        <motion.p
          className="text-lg sm:text-xl leading-relaxed text-slate-700 text-center mb-6 max-w-2xl mx-auto"
          data-testid="concept-teach-narration"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.35 }}
        >
          {words.map((w, i) => {
            const isWs = /^\s+$/.test(w);
            if (isWs) return <span key={i}>{w}</span>;
            const active = i === wordIdx;
            return (
              <span
                key={i}
                className={active ? 'bg-amber-200 rounded px-0.5 transition-colors' : 'transition-colors'}
                data-testid={active ? 'concept-teach-active-word' : undefined}
              >
                {w}
              </span>
            );
          })}
        </motion.p>

        {/* Examples strip — pop in one by one */}
        {Array.isArray(lesson.examples) && lesson.examples.length > 0 && (
          <div className="flex flex-wrap justify-center gap-2 mb-6" data-testid="concept-teach-examples">
            {lesson.examples.map((ex, i) => (
              <motion.span
                key={i}
                className="inline-flex items-center gap-1 rounded-full bg-white/80 border border-teal-200 px-3 py-1 text-sm text-teal-800 shadow-sm"
                initial={{ opacity: 0, scale: 0.6, y: 6 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ type: 'spring', stiffness: 300, damping: 18, delay: 0.5 + i * 0.08 }}
              >
                {ex.label}
              </motion.span>
            ))}
          </div>
        )}

        {/* YouTube (if provided) */}
        {lesson.youtube ? (
          <motion.div
            className="mb-6 max-w-xl mx-auto aspect-video rounded-xl overflow-hidden border border-slate-200 shadow-sm"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.8 }}
          >
            <iframe
              title={lesson.title}
              src={`${lesson.youtube}?rel=0&modestbranding=1`}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              className="w-full h-full"
              data-testid="concept-teach-youtube"
            />
          </motion.div>
        ) : lesson.youtube_search ? (
          <motion.div
            className="mb-6 max-w-xl mx-auto"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.8 }}
          >
            <a
              href={`https://www.youtube.com/results?search_query=${encodeURIComponent(lesson.youtube_search)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 transition-colors text-sm font-medium"
              data-testid="concept-teach-youtube-search"
            >
              <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.004 3.004 0 0 0-2.113-2.126C19.464 3.5 12 3.5 12 3.5s-7.464 0-9.385.56A3.004 3.004 0 0 0 .502 6.186C0 8.1 0 12 0 12s0 3.9.502 5.814c.28 1.04 1.096 1.858 2.113 2.126C4.536 20.5 12 20.5 12 20.5s7.464 0 9.385-.56a3.004 3.004 0 0 0 2.113-2.126C24 15.9 24 12 24 12s0-3.9-.502-5.814zM9.75 15.568V8.432L15.818 12 9.75 15.568z"/></svg>
              Watch kid-safe videos for this concept on YouTube
            </a>
          </motion.div>
        ) : null}

        {/* Actions */}
        <motion.div
          className="flex flex-wrap items-center justify-center gap-3"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.9 }}
        >
          <Button
            variant="outline"
            size="lg"
            onClick={replay}
            disabled={isSpeaking}
            className="h-14 px-5 rounded-full border-teal-300 text-teal-800 bg-white/70 backdrop-blur"
            data-testid="concept-teach-replay-btn"
          >
            <Volume2 className="w-5 h-5 mr-2" />
            {isSpeaking ? 'Reading…' : 'Read again'}
          </Button>
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.96 }}>
            <Button
              size="lg"
              onClick={handleGotIt}
              className="h-14 px-8 rounded-full bg-teal-600 hover:bg-teal-700 text-white text-lg shadow-lg shadow-teal-500/30"
              data-testid="concept-teach-got-it-btn"
            >
              <Check className="w-5 h-5 mr-2" />
              I got it!
            </Button>
          </motion.div>
        </motion.div>

        {/* Small "Play once more" hint for very young kids */}
        <p className="text-center text-xs text-slate-400 mt-4 flex items-center justify-center gap-1">
          <PlayCircle className="w-3 h-3" /> {lesson.source === 'ai' ? 'AI-generated lesson' : 'Zazu concept lesson'}
        </p>
      </motion.div>
    </AnimatePresence>
  );
};

export default ConceptTeachCard;
